import { useEffect, useState } from "react";
import { History, RotateCcw, Eye, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
    adminListArticleRevisions,
    adminGetArticleRevision,
    adminRestoreArticleRevision,
} from "@/lib/api";

function formatWhen(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleString();
    } catch {
        return iso;
    }
}

/**
 * Revision history panel for the CMS article editor.
 *
 * Lists the most recent snapshots (newest first) and lets the admin preview
 * any single revision in a slide-out OR restore it (the restore endpoint
 * snapshots the current state first, so it's reversible).
 */
export default function RevisionHistoryPanel({ slug, onRestored }) {
    const [revisions, setRevisions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState(null);  // full revision object
    const [restoring, setRestoring] = useState(null);  // revision_id being restored

    const load = async () => {
        if (!slug) return;
        try {
            setLoading(true);
            const rows = await adminListArticleRevisions(slug, 25);
            setRevisions(rows);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load revisions.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [slug]);  // eslint-disable-line react-hooks/exhaustive-deps

    const handlePreview = async (rev) => {
        try {
            const full = await adminGetArticleRevision(slug, rev.revision_id);
            setPreview(full);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load revision.");
        }
    };

    const handleRestore = async (rev) => {
        if (!window.confirm(`Restore the article to this revision from ${formatWhen(rev.created_at)}? Your current draft will be snapshotted first so this is reversible.`)) {
            return;
        }
        try {
            setRestoring(rev.revision_id);
            await adminRestoreArticleRevision(slug, rev.revision_id);
            toast.success("Restored. The editor will refresh.");
            onRestored?.();
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Restore failed.");
        } finally {
            setRestoring(null);
        }
    };

    if (!slug) return null;

    return (
        <section
            className="bl-card p-5 space-y-3"
            data-testid="cms-revision-history-panel"
        >
            <div className="flex items-center justify-between gap-2">
                <div>
                    <div className="bl-eyebrow">
                        <History className="w-3.5 h-3.5" /> History
                    </div>
                    <h3 className="font-display font-semibold text-lg text-bl mt-1">
                        Revisions
                    </h3>
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="text-xs text-bl-muted hover:text-bl"
                    data-testid="cms-revisions-refresh"
                >
                    Refresh
                </button>
            </div>

            {loading ? (
                <div className="text-xs text-bl-muted flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                </div>
            ) : revisions.length === 0 ? (
                <p className="text-xs text-bl-muted">
                    No revisions yet. Each save creates a snapshot.
                </p>
            ) : (
                <ul className="space-y-1.5 max-h-72 overflow-y-auto">
                    {revisions.map((r) => (
                        <li
                            key={r.revision_id}
                            className="bl-card p-2.5 flex items-center justify-between gap-2"
                            data-testid={`cms-revision-${r.revision_id}`}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="text-xs text-bl truncate">
                                    {r.title || "(untitled)"}
                                </div>
                                <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted">
                                    {formatWhen(r.created_at)}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => handlePreview(r)}
                                className="text-bl-muted hover:text-bl p-1.5"
                                aria-label="Preview"
                                title="Preview"
                                data-testid={`cms-revision-preview-${r.revision_id}`}
                            >
                                <Eye className="w-3.5 h-3.5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => handleRestore(r)}
                                disabled={restoring === r.revision_id}
                                className="text-bl-primary hover:text-bl-primary/80 p-1.5 disabled:opacity-50"
                                aria-label="Restore"
                                title="Restore"
                                data-testid={`cms-revision-restore-${r.revision_id}`}
                            >
                                {restoring === r.revision_id ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                    <RotateCcw className="w-3.5 h-3.5" />
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {/* Preview drawer */}
            {preview && (
                <div
                    className="fixed inset-0 z-50 bg-bl-bg/70 backdrop-blur-sm"
                    onClick={() => setPreview(null)}
                    data-testid="cms-revision-preview-overlay"
                >
                    <div
                        className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-bl-paper border-l border-bl shadow-xl flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-5 border-b border-bl flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="bl-eyebrow">Preview</div>
                                <h4 className="font-display font-semibold text-bl text-base mt-1 truncate">
                                    {preview.title}
                                </h4>
                                <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted mt-1">
                                    {formatWhen(preview.created_at)}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setPreview(null)}
                                className="text-bl-muted hover:text-bl"
                                aria-label="Close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div
                            className="flex-1 overflow-y-auto p-5 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: preview.body || "" }}
                            data-testid="cms-revision-preview-body"
                        />
                        <div className="p-4 border-t border-bl flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setPreview(null)}
                                className="bl-btn-ghost text-sm"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const r = revisions.find((x) => x.revision_id === preview.revision_id);
                                    if (r) handleRestore(r);
                                    setPreview(null);
                                }}
                                className="bl-btn-primary text-sm"
                                data-testid="cms-revision-restore-from-preview"
                            >
                                <RotateCcw className="w-3.5 h-3.5" /> Restore this version
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
